/**
 * Export utilities — markdown download and ZIP export.
 * Adapted from Millstone prototype helpers.
 */

import JSZip from 'jszip';
import FileSaver from 'file-saver';
import { toFilename } from './format';

// ESM/CJS compat
const saveAs = (FileSaver as any).saveAs || FileSaver;

export interface ExportableMessage {
  role: 'user' | 'model';
  text: string;
  attachments?: Array<{ mimeType: string; data: string }>;
}

export interface ExportableThread {
  title: string;
  createdAt: number;
  model?: string;
  messages: ExportableMessage[];
}

export interface ExportableFolder {
  name: string;
  description?: string;
  instructions?: string;
  files?: Array<{ name: string; data: string }>;
}

export interface ExportableAgent {
  name: string;
  [key: string]: unknown;
}

/**
 * Download a single thread as a Markdown file.
 */
export function downloadThreadAsMarkdown(
  thread: ExportableThread,
  projectName?: string,
  agentName?: string,
): void {
  let content = `# ${thread.title}\n\n`;
  content += `**Date:** ${new Date(thread.createdAt).toLocaleString()}\n`;
  if (projectName) content += `**Project:** ${projectName}\n`;
  if (agentName) content += `**Agent:** ${agentName}\n`;
  if (thread.model) content += `**Model:** ${thread.model}\n`;
  content += `\n---\n\n`;

  thread.messages.forEach((msg) => {
    content += `### ${msg.role === 'user' ? 'User' : 'Assistant'}\n`;
    content += `${msg.text}\n\n`;
    if (msg.attachments?.length) {
      content += `*[${msg.attachments.length} attachment(s)]*\n\n`;
    }
  });

  const blob = new Blob([content], { type: 'text/markdown;charset=utf-8' });
  saveAs(blob, `${toFilename(thread.title)}.md`);
}

/**
 * Export a project (folder + threads + agents) as a ZIP file.
 */
export async function exportProjectToZip(
  folder: ExportableFolder,
  threads: ExportableThread[],
  agents: ExportableAgent[],
): Promise<void> {
  const zip = new JSZip();
  const folderName = toFilename(folder.name);

  zip.file('project_metadata.json', JSON.stringify(folder, null, 2));

  if (folder.files) {
    const filesDir = zip.folder('knowledge_base');
    folder.files.forEach((f) => {
      filesDir?.file(f.name, f.data, { base64: true });
    });
  }

  const chatsDir = zip.folder('chats');
  threads.forEach((t) => {
    const safeName = toFilename(t.title);
    chatsDir?.file(`${safeName}.json`, JSON.stringify(t, null, 2));
    let md = `# ${t.title}\n\n`;
    t.messages.forEach((m) => {
      md += `**${m.role.toUpperCase()}:** ${m.text}\n\n`;
    });
    chatsDir?.file(`${safeName}.md`, md);
  });

  const agentsDir = zip.folder('agents');
  agents.forEach((a) => {
    agentsDir?.file(`${toFilename(a.name)}.json`, JSON.stringify(a, null, 2));
  });

  const content = await zip.generateAsync({ type: 'blob' });
  saveAs(content, `${folderName}_export.zip`);
}
